import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Card, Select, Stack, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import { Can } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import { fetchOrgUsers, orgJoinSelectLabel } from "./organizationMemberShared";
import AssessmentViewerPicker from "./AssessmentViewerPicker";
import { orgInstrumentSelectOptions } from "./orgInstrumentSelectOptions";

export default function OrganizationAssignmentRequestCreate({
  organizationId,
  onSuccess = () => {},
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState(null);
  const [instrumentId, setInstrumentId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [instrumentOptions, setInstrumentOptions] = useState([]);
  const [viewerKeys, setViewerKeys] = useState([]);
  const [viewerIds, setViewerIds] = useState({
    organization_user_admin_ids: [],
    organization_user_manager_ids: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoadingOptions(true);
    Promise.all([
      fetchOrgUsers(organizationId),
      axios.post(
        `/organizations/${organizationId}/retrieve-assessment-instruments/`,
        { page: 0, page_size: 200 }
      ),
      axios.post(`/retrieve-assessment-instruments/`, {
        scope: "active",
        page: 0,
        page_size: 200,
      }),
    ])
      .then(([users, instrumentsRes, catalogRes]) => {
        setUserOptions(
          (users || []).map((row) => ({
            value: String(row.user_id),
            label: orgJoinSelectLabel(row, row.user_id),
          }))
        );
        const joined = instrumentsRes.data.response[0]?.data || [];
        const catalog = catalogRes.data.response[0]?.data || [];
        setInstrumentOptions(orgInstrumentSelectOptions(joined, catalog, t));
        setLoadingOptions(false);
      })
      .catch((err) => {
        setLoadingOptions(false);
        toast.error(err);
      });
  }, [organizationId]);

  function submit() {
    if (!userId || !instrumentId) return;
    setSubmitting(true);
    const body = {
      user_id: userId,
      assessment_instrument_id: instrumentId,
    };
    if (viewerIds.organization_user_admin_ids.length) {
      body.organization_user_admin_ids = viewerIds.organization_user_admin_ids;
    }
    if (viewerIds.organization_user_manager_ids.length) {
      body.organization_user_manager_ids =
        viewerIds.organization_user_manager_ids;
    }

    axios
      .post(
        `/organizations/${organizationId}/assessment-assignment-requests/`,
        body
      )
      .then(() => {
        setSubmitting(false);
        setUserId(null);
        setInstrumentId(null);
        setViewerKeys([]);
        setViewerIds({
          organization_user_admin_ids: [],
          organization_user_manager_ids: [],
        });
        toast.success(t("created"));
        onSuccess();
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(err);
      });
  }

  return (
    <Can permission={PERMISSIONS.ASSESSMENTS_REQUEST}>
      <Card>
        <Title order={5} mb="sm">
          {t("assignment_request_create")}
        </Title>
        <Stack gap="sm">
          <Select
            label={t("user_singular")}
            searchable
            clearable
            data={userOptions}
            value={userId}
            onChange={(value) => {
              setUserId(value);
              setViewerKeys([]);
              setViewerIds({
                organization_user_admin_ids: [],
                organization_user_manager_ids: [],
              });
            }}
            disabled={loadingOptions}
          />
          <Select
            label={t("assessment_instrument_singular")}
            searchable
            clearable
            data={instrumentOptions}
            value={instrumentId}
            onChange={setInstrumentId}
            disabled={loadingOptions}
          />
          <AssessmentViewerPicker
            organizationId={organizationId}
            userId={userId}
            mode="multi"
            value={viewerKeys}
            onChange={({ keys, organization_user_admin_ids, organization_user_manager_ids }) => {
              setViewerKeys(keys || []);
              setViewerIds({
                organization_user_admin_ids: organization_user_admin_ids || [],
                organization_user_manager_ids:
                  organization_user_manager_ids || [],
              });
            }}
          />
          <Button
            leftSection={<IconPlus size={14} />}
            disabled={!userId || !instrumentId}
            loading={submitting}
            onClick={submit}
            fullWidth
          >
            {t("create")}
          </Button>
        </Stack>
      </Card>
    </Can>
  );
}
